import type { ChannelType, Market, RateRule, UserTier } from '../dataProvider';

/**
 * THE MIRRORED RESOLVER (D-rate-rules).
 *
 * A pure function that, given a catalog target (a store or a specific channel),
 * the rules, an injected clock and a tier, returns the effective rate and WHICH
 * rule won. It MIRRORS the platform rate-rule contract (RATE-RULES.md in the web
 * monorepo) EXACTLY: the real shared resolver `@cashblack/contracts/rate-rules`
 * is the cross-repo SWAP SEAM (the same posture the data provider takes toward
 * the real admin API). The admin panel is a separate repo, so the contract is not
 * a dependency here; this file is the faithful mirror, pinned by golden tests.
 *
 * THE ALGORITHM (verbatim contract order):
 *   1. Pull all rules whose SCOPE matches the target.
 *   2. Keep rules ACTIVE NOW: status active AND now within [visibleFrom, visibleUntil]
 *      (a null bound is open). Derived off the INJECTED clock.
 *   3. Keep rules whose tierEligibility includes the resolving tier.
 *   4. Keep rules whose market is the target market OR 'all'.
 *   5. Sort by priority DESCENDING; tie-break by RECENCY (updatedAt then createdAt,
 *      descending). The highest priority wins.
 *   6. WINNER-TAKES-ONE: apply ONLY the winning rule's effect to the base rate.
 *      Rules DO NOT stack.
 *   7. Return the effective rate, the winner and the ranked contenders.
 *
 * This is NOT the live per-click resolution (which runs server-side per click).
 * The admin uses it for a PROJECTION over today's catalog and rules.
 */

/**
 * The catalog row being resolved. `channelType` is the channel being resolved, or
 * null for a region-level resolution (no specific channel) — marketplace-shaped
 * scopes only match a concrete channel. `baseRate` is the channel baseRate when a
 * channel is resolved, else the region baseRate.
 */
export interface ResolutionTarget {
  /** The merchant_regions id (the store row). */
  regionId: number;
  market: Market;
  /** The brand's category (merchants.categoryId — a brand-level hop). */
  categoryId: number;
  /** The region's tag keys (the derived mirror of the join). */
  tagKeys: readonly string[];
  /** The channel being resolved, or null for region-level resolution. */
  channelType: ChannelType | null;
  /** The base rate the effect transforms (channel baseRate, else region baseRate). */
  baseRate: number;
}

export interface ResolutionResult {
  baseRate: number;
  effectiveRate: number;
  /** The rule that won, or null when nothing matched (effectiveRate === baseRate). */
  winner: RateRule | null;
  /** Every rule that matched scope + active + tier + market, sorted winner-first. */
  contenders: RateRule[];
}

const round4 = (n: number): number => Math.round(n * 10000) / 10000;

/** Does the rule's SCOPE match the target (step 1)? */
export const scopeMatches = (
  rule: RateRule,
  target: ResolutionTarget,
): boolean => {
  const ref = rule.scopeRef;
  switch (rule.scopeType) {
    case 'store':
      return ref === target.regionId;
    case 'marketplace':
      return target.channelType != null && ref === target.channelType;
    case 'category':
      return ref === target.categoryId;
    case 'marketplace_category': {
      const r = ref as { channelType: ChannelType; categoryId: number };
      return (
        target.channelType != null &&
        r.channelType === target.channelType &&
        r.categoryId === target.categoryId
      );
    }
    case 'tag':
      return target.tagKeys.includes(ref as string);
    case 'tag_marketplaces': {
      const r = ref as { tag: string; marketplaces: ChannelType[] };
      return (
        target.tagKeys.includes(r.tag) &&
        target.channelType != null &&
        r.marketplaces.includes(target.channelType)
      );
    }
    case 'store_list':
      return (ref as number[]).includes(target.regionId);
    case 'market':
      return ref === target.market;
    default:
      return false;
  }
};

/** Is the rule live at `now` (status active AND within its window)? (step 2) */
export const isLiveAt = (rule: RateRule, now: number): boolean => {
  if (rule.status !== 'active') return false;
  if (rule.visibleFrom != null && now < Date.parse(rule.visibleFrom)) {
    return false;
  }
  if (rule.visibleUntil != null && now > Date.parse(rule.visibleUntil)) {
    return false;
  }
  return true;
};

/** Apply ONE rule's effect to the base rate (step 6, the four effects). */
export const applyEffect = (baseRate: number, rule: RateRule): number => {
  switch (rule.effectType) {
    case 'multiplier':
      return round4(baseRate * rule.effectValue);
    case 'absolute':
      return round4(rule.effectValue);
    case 'cap':
      return round4(Math.min(baseRate, rule.effectValue));
    case 'floor':
      return round4(Math.max(baseRate, rule.effectValue));
    default:
      return baseRate;
  }
};

/** Recency key for the tie-break (updatedAt, falling back to createdAt). */
const recency = (rule: RateRule): number =>
  Date.parse(rule.updatedAt ?? rule.createdAt) || 0;

/**
 * Resolve the effective rate for one target against all rules at `now` for `tier`.
 * Winner-takes-one: only the highest-priority matching rule applies; ties break
 * by recency. Returns the ranked contenders so the conflict preview can explain
 * the ordering truthfully.
 */
export const resolveEffectiveRate = (
  target: ResolutionTarget,
  rules: readonly RateRule[],
  now: number,
  tier: UserTier,
): ResolutionResult => {
  const contenders = rules
    .filter((r) => scopeMatches(r, target)) // 1
    .filter((r) => isLiveAt(r, now)) // 2
    .filter((r) => r.tierEligibility.includes(tier)) // 3
    .filter((r) => r.market === target.market || r.market === 'all') // 4
    .sort((a, b) => b.priority - a.priority || recency(b) - recency(a)); // 5

  const winner = contenders[0] ?? null; // 6 winner-takes-one
  const effectiveRate = winner
    ? applyEffect(target.baseRate, winner)
    : round4(target.baseRate);

  return {
    baseRate: round4(target.baseRate),
    effectiveRate,
    winner,
    contenders,
  };
};
